import React, { useState, useEffect, useRef } from 'react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppSelect from 'src/components/common/AppSelect';
import DataTable from 'src/components/ui/data-table';
import {
  useInvoiceList,
  useBillingSummary,
} from 'src/services/api/queries/billingQueries';
import { FileText, IndianRupee, Clock, CheckCircle, Info } from 'lucide-react';
import BillingTabs from 'src/pages/Billing/BillingTabs/BillingTabs';
import SearchInput from 'src/components/common/SearchInput';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from 'src/components/ui/tooltip';
import { StatusBadge } from 'src/components/common/StatusBadge';
import StatsSection from 'src/components/common/StatsSection';

const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `${num.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatCompact = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  if (abs >= 1_00_00_000)
    return `${sign}${(abs / 1_00_00_000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  if (abs >= 1_00_000)
    return `${sign}${(abs / 1_00_000).toFixed(2).replace(/\.?0+$/, '')}L`;
  if (abs >= 1_000)
    return `${sign}${(abs / 1_000).toFixed(2).replace(/\.?0+$/, '')}K`;
  return `${sign}${abs.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getColumns = () => [
  {
    key: 'invoice_number',
    header: 'Invoice',
    width: '28%',
    render: (value, row) => (
      <div>
        <div className="font-mono text-[12.5px] font-semibold text-primary tracking-[0.02em] leading-tight">
          {value}
        </div>
        {row.organization_name && (
          <div className="text-[12px] text-muted-foreground font-[450] mt-0.5 leading-tight">
            {row.organization_name}
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'billing_date',
    header: 'Billing Date',
    width: '14%',
    render: (value) => (
      <span
        className="text-[12.5px] text-muted-foreground font-[450]"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {formatDate(value)}
      </span>
    ),
  },
  {
    key: 'due_date',
    header: 'Due Date',
    width: '14%',
    render: (value) => (
      <span
        className="text-[12.5px] text-muted-foreground font-[450]"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {formatDate(value)}
      </span>
    ),
  },
  {
    key: 'total_amount',
    header: (
      <div className="flex items-center gap-1 justify-end w-full">
        Amount
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="p-0 m-0 bg-transparent border-0 cursor-pointer"
                aria-label="GST exclusive"
              >
                <Info className="w-3.5 h-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>GST exclusive</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
    width: '18%',
    align: 'right',
    render: (value) => (
      <span
        className="text-[13px] font-semibold text-foreground-strong whitespace-nowrap"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {formatAmount(value)}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: '14%',
    render: (value) => <StatusBadge value={value} />,
  },
];

const DEFAULT_PAGE_SIZE = 10;

function ListInvoices() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Sort — sortBy is fixed to 'created_at' for now (no column-picker yet,
  // just the one field), sortOrder is the only thing the dropdown actually
  // controls.
  const [sortOrder, setSortOrder] = useState('desc');
  const prevFiltersRef = useRef({
    search: '',
    status: 'all',
    sortOrder: 'desc',
  });

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentPageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );

  // Server-side list: page / size / search / status are all part of the
  // queryKey, so changing any of them refetches automatically.
  const { data: invoiceResponse, isFetching } = useInvoiceList({
    page: currentPage,
    pageSize: currentPageSize,
    search: debouncedSearch || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    sortBy: 'created_at',
    sortOrder,
  });
  // Real totals across the whole dataset, not just the current page — see
  // BillingService.getBillingSummary.
  const { data: summaryData } = useBillingSummary('invoice');
  const invoiceSummary = summaryData?.data?.invoice ?? {};

  const invoices =
    invoiceResponse?.data?.invoices || invoiceResponse?.invoices || [];
  const metrics = invoiceResponse?.data?.metrics ?? null;
  const pagination =
    invoiceResponse?.data?.pagination || invoiceResponse?.pagination || {};
  const totalItems = pagination.total ?? 0;
  const totalPages =
    pagination.total_pages ||
    Math.ceil((totalItems || 0) / (currentPageSize || DEFAULT_PAGE_SIZE)) ||
    1;

  const columns = getColumns();

  const updateUrlParams = (params) => {
    const updatedParams = new URLSearchParams(searchParams);
    if (params.page) updatedParams.set('page', params.page.toString());
    if (params.pageSize)
      updatedParams.set('pageSize', params.pageSize.toString());
    setSearchParams(updatedParams);
  };

  const handlePageChange = (newPage) => {
    updateUrlParams({ page: parseInt(newPage, 10) });
  };

  const handlePageSizeChange = (newPageSize) => {
    updateUrlParams({ pageSize: parseInt(newPageSize, 10), page: 1 });
  };

  // Debounce the search box before hitting the API
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    if (!searchParams.has('pageSize')) {
      updateUrlParams({ pageSize: DEFAULT_PAGE_SIZE });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Server-side fetch whenever page, size, search, or status changes.
  // When a filter changes we snap back to page 1 first, then fetch.
  useEffect(() => {
    const filtersChanged =
      prevFiltersRef.current.search !== debouncedSearch ||
      prevFiltersRef.current.status !== statusFilter ||
      prevFiltersRef.current.sortOrder !== sortOrder;
    prevFiltersRef.current = {
      search: debouncedSearch,
      status: statusFilter,
      sortOrder,
    };

    if (filtersChanged && currentPage !== 1) {
      updateUrlParams({ page: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, currentPageSize, debouncedSearch, statusFilter, sortOrder]);

  const hasFilters = statusFilter !== 'all' || searchQuery.trim() !== '';

  const paidCount = invoiceSummary.status_breakdown?.PAID ?? 0;
  const awaitedCount = invoiceSummary.status_breakdown?.PAYMENT_AWAITED ?? 0;
  const totalValue = invoiceSummary.total_amount ?? 0;

  const emptyState = (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="flex flex-col items-center justify-center py-16 px-5 text-center">
        <div
          className="flex items-center justify-center mb-5 rounded-full bg-border"
          style={{ width: 72, height: 72 }}
        >
          <FileText
            className="text-muted-foreground"
            style={{ width: 32, height: 32, strokeWidth: 1.5 }}
          />
        </div>
        <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-[-0.01em]">
          No invoices found
        </p>
        <p className="text-[13px] text-muted-foreground leading-relaxed max-w-xs">
          {hasFilters
            ? 'Try adjusting your filters or search query.'
            : 'Invoices will appear here once they are generated.'}
        </p>
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader title="Invoices" />

      <PageContainer>
        {/* Stats section */}
        <StatsSection
          title="Summary"
          className="mb-5"
          cards={[
            {
              key: 'total',
              label: 'Total Invoices',
              value: invoiceSummary.total_count ?? totalItems ?? 0,
              iconColor: 'var(--primary)',
              icon: (
                <FileText style={{ width: 20, height: 20, strokeWidth: 1.8 }} />
              ),
            },
            {
              key: 'awaited',
              label: 'Payment Awaited',
              value: awaitedCount,
              iconColor: '#B45309',
              icon: (
                <Clock style={{ width: 20, height: 20, strokeWidth: 1.8 }} />
              ),
            },
            {
              key: 'paid',
              label: 'Paid',
              value: paidCount,
              iconColor: '#0F766E',
              icon: (
                <CheckCircle
                  style={{ width: 20, height: 20, strokeWidth: 1.8 }}
                />
              ),
            },
            {
              key: 'value',
              label: 'Total Value',
              value: formatCompact(totalValue),
              iconColor: '#7C3AED',
              icon: (
                <IndianRupee
                  style={{ width: 20, height: 20, strokeWidth: 1.8 }}
                />
              ),
            },
          ]}
        />

        <BillingTabs metrics={metrics} />

        {/* Filter bar */}
        <div className="flex items-center gap-2.5 mb-4">
          <AppSelect
            value={statusFilter}
            onValueChange={setStatusFilter}
            options={[
              { value: 'all', label: 'All Statuses' },
              { value: 'GENERATED', label: 'Generated' },
              { value: 'PAYMENT_AWAITED', label: 'Payment Awaited' },
              { value: 'PAID', label: 'Paid' },
              { value: 'MODIFIED', label: 'Modified' },
            ]}
            className="h-auto py-2 text-[12.5px] font-[500] w-[180px]"
          />
          <AppSelect
            value={sortOrder}
            onValueChange={setSortOrder}
            options={[
              { value: 'desc', label: 'Sort: Newest first' },
              { value: 'asc', label: 'Sort: Oldest first' },
            ]}
            className="h-auto py-2 text-[12.5px] font-[500] w-[170px]"
          />
          <div className="flex-1" />
          <SearchInput
            value={searchQuery}
            onValueChange={setSearchQuery}
            placeholder="Search invoices..."
            className="w-52"
          />
        </div>

        <DataTable
          data={invoices}
          columns={columns}
          loading={isFetching}
          rowKey="id"
          className="border-border shadow-[0_1px_3px_rgba(0,0,0,.04)]"
          hover
          stickyHeader
          tableLayout="fixed"
          onRowClick={(row) => navigate(`/invoices/${row?.id}`)}
          pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={currentPageSize}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          emptyState={emptyState}
        />
      </PageContainer>
    </div>
  );
}

export default ListInvoices;
